import { Link } from "react-router-dom";
import { Handshake, Sparkles, UserPlus, Wallet } from "lucide-react";
import { useSession } from "@/auth/SessionProvider";
import { openSupportAgentBubble } from "@/components/Support/SupportAgentBubble";

const PRIMARY_JOBS = [
  {
    title: "Create a lead",
    description: "Capture your first pipeline opportunity in CRM.",
    href: "/crm/leads/new",
    icon: Handshake,
  },
  {
    title: "Add an account and invoice",
    description: "Create a Finance account, then issue your first invoice.",
    href: "/finance/customers/new",
    icon: Wallet,
  },
  {
    title: "Invite a teammate",
    description: "Add a user and share a sign-in link for your org.",
    href: "/settings/users",
    icon: UserPlus,
  },
];

type Props = {
  isTrial?: boolean;
};

export function GettingStartedPanel({ isTrial }: Props) {
  const session = useSession();
  const name = session.data?.user?.name?.split(" ")[0] || "there";

  return (
    <div className="rounded-lg border border-indigo-100 bg-gradient-to-br from-indigo-50/80 to-white p-5 shadow-sm dark:border-indigo-500/20 dark:from-indigo-950/40 dark:to-zinc-900">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {isTrial ? `Welcome, ${name}` : "First jobs"}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {isTrial
              ? "Pick one tile to get started, or open Ask One AI in the bar below."
              : "Quick paths into CRM, Finance, and people setup."}
          </p>
        </div>
        <button
          type="button"
          onClick={() => openSupportAgentBubble()}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-emerald-800/30 bg-white px-3 py-1.5 text-xs font-semibold text-[#0b3d2e] shadow-sm hover:bg-emerald-50 dark:border-emerald-500/40 dark:bg-zinc-800 dark:text-emerald-200"
        >
          <Sparkles className="size-3.5" aria-hidden />
          Ask One AI
        </button>
      </div>
      <ul className="mt-4 grid gap-3 sm:grid-cols-3">
        {PRIMARY_JOBS.map(({ title, description, href, icon: Icon }) => (
          <li key={href}>
            <Link
              to={href}
              className="flex h-full flex-col rounded-lg border border-gray-200 bg-white/90 p-4 transition hover:border-indigo-200 hover:shadow-sm dark:border-white/10 dark:bg-zinc-950/50"
            >
              <Icon className="size-5 text-indigo-600 dark:text-indigo-400" aria-hidden />
              <p className="mt-2 font-medium text-gray-900 dark:text-white">{title}</p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{description}</p>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
